/**
 * Helper class containing utility functions
 */
export class Utils {
    /**
     * Checks if a value is null or undefined
     * @param value The value to check
     * @returns True if the value is null or undefined, false otherwise
     */
    public static isNullOrUndefined(value: any): boolean {
        return value === null || value === undefined;
    }

    /**
     * Checks if a string is null, undefined or empty
     * @param value The string to check
     * @returns True if the string is null, undefined or empty, false otherwise
     */
    public static isNullOrEmpty(value: string | null | undefined): boolean {
        return this.isNullOrUndefined(value) || value?.trim() === '';
    }

    /**
     * Checks if a string has a value
     * @param value The string to check
     * @returns True if the string has a value, false otherwise
     */
    public static hasValue(value: string | null | undefined): boolean {
        return !this.isNullOrEmpty(value);
    }

    /**
     * Safely parses a string to a number, returns null if parsing fails
     * @param value The string to parse
     * @returns The parsed number or null if parsing failed
     */
    public static parseNumber(value: string): number | null {
        const parsed = Number(value);
        return isNaN(parsed) ? null : parsed;
    }

    /**
     * Formats a date to a localized string (Swedish format)
     * @param date The date to format
     * @returns The formatted date string
     */
    public static formatDate(date: Date): string {
        return date.toLocaleDateString('sv-SE');
    }

    /**
     * Checks if a date is initialized
     * @param date The date to check
     * @returns True if the date is initialized, false otherwise
     */
    public static isDateInitialized(date: Date | string | null | undefined): boolean {
        if (!date) return false;

        // Handle Date objects that are invalid
        if (date instanceof Date && isNaN(date.getTime())) {
            return false;
        }
        
        // Handle string
        const compareDate = new Date(date);
        if (isNaN(compareDate.getTime())) return false;
        
        const defaultDate = new Date('0001-01-01T00:00:00');
        return compareDate.getTime() !== defaultDate.getTime();
    }
}
